import type { StatusSegment } from "../StatusBreakdownChart";
import type { AiProviderModelCostRow } from "./aiProviderMonitoring";
import { formatEstimatedCostUsd } from "./aiUsage";
import {
  AI_USAGE_ACTIVITIES,
  AI_USAGE_ACTIVITY_LABELS,
} from "./aiUsageActivities";
import { formatModelName } from "../../lib/formatModelName";
import { splitBySharePrefix } from "./foldSegments";
import { SPACE_COLORS } from "./monitoringPanelLayout";

export const OTHER_MODELS_KEY = "__other_models__";

// Models below this share of total spend (or beyond MAX_KEPT) fold into a single
// "Other" group, so a long tail of negligible models doesn't clutter the chart.
// MAX_KEPT stays below SPACE_COLORS.length so kept segments never reuse a color
// (splitBySharePrefix can keep one extra to avoid an "Other" of one).
const MIN_SHARE = 0.03;
const MAX_KEPT = 7;

function modelDetails(
  row: AiProviderModelCostRow
): { label: string; value: string }[] {
  const activityCost = AI_USAGE_ACTIVITIES.reduce(
    (sum, activity) => sum + (row.byActivity[activity].estimatedCostUsd ?? 0),
    0
  );
  // Spend from sources that aren't build/run/monitor (personal chat, value
  // estimates); surfaced so the activity rows reconcile with Total.
  const otherCost = (row.estimatedCostUsd ?? 0) - activityCost;
  return [
    { label: "Total", value: formatEstimatedCostUsd(row.estimatedCostUsd) },
    ...AI_USAGE_ACTIVITIES.map((activity) => ({
      label: AI_USAGE_ACTIVITY_LABELS[activity],
      value: formatEstimatedCostUsd(
        row.byActivity[activity].estimatedCostUsd || null
      ),
    })),
    ...(otherCost > 0.005
      ? [{ label: "Other", value: formatEstimatedCostUsd(otherCost) }]
      : []),
    { label: "Calls", value: row.requestCount.toLocaleString("en-US") },
  ];
}

// modelRows must be sorted by spend desc (computeModelCostRows does this). The
// small-spend tail rolls up into one "Other" segment listing the folded models.
export function buildModelSpendSegments(
  modelRows: readonly AiProviderModelCostRow[]
): { segments: StatusSegment[]; total: number } {
  const total = modelRows.reduce(
    (sum, row) => sum + (row.estimatedCostUsd ?? 0),
    0
  );
  const { kept, folded } = splitBySharePrefix(
    modelRows,
    (row) => row.estimatedCostUsd ?? 0,
    total,
    { minShare: MIN_SHARE, maxKept: MAX_KEPT }
  );
  const segments: StatusSegment[] = kept.map((row, i) => ({
    key: row.model,
    count: row.estimatedCostUsd ?? 0,
    color: SPACE_COLORS[i % SPACE_COLORS.length]!,
    label: formatModelName(row.model),
    details: modelDetails(row),
  }));
  if (folded.length > 0) {
    segments.push({
      key: OTHER_MODELS_KEY,
      count: folded.reduce((sum, row) => sum + (row.estimatedCostUsd ?? 0), 0),
      color: "var(--gray-600)",
      label: `${folded.length} more models`,
      details: folded.map((row) => ({
        label: formatModelName(row.model),
        value: formatEstimatedCostUsd(row.estimatedCostUsd),
      })),
    });
  }
  return { segments, total };
}
